import React, {useMemo} from 'react'
import {getLayoutData, getPageData, getPages, getWWDType} from "@/pages/api/api";
import {
	CONTEXT_BASED_BANNER_QUERY,
	RECAP_INFO_QUERY,
	RECAP_SUMMARY_ITEM_QUERY,
	SEE_ALSO_INFO_BLOCK_QUERY,
	SEO_QUERY,
	STATE_QUERY,
	WHAT_WE_DO_CATEGORY_QUERY,
	WHAT_WE_DO_SINGLE_ACTION_QUERY
} from "@/pages/api/fragments";
import {CONTENT_TYPES, PAGES} from "@/constants";
import dynamic from "next/dynamic";
import Loading from "@/components/common/Loading";
import PreviewTag from "@/components/common/PreviewTag";
import {getItemType} from "@/utils/utils";

const WhatWeDoCategoryPageTemplate = dynamic(() => import('@/templates/WhatWeDoCategoryPageTemplate'), {
	loading: () => <Loading />,
	ssr: false
});

const WhatWeDoSingleActionPageTemplate = dynamic(() => import('@/templates/WhatWeDoSingleActionPageTemplate'), {
	loading: () => <Loading />,
	ssr: false
});

function getTemplate(data) {
	const type = getItemType(data)
	switch (type) {
		case CONTENT_TYPES.WHAT_WE_DO_CATEGORY:
			return WhatWeDoCategoryPageTemplate
		case CONTENT_TYPES.WHAT_WE_DO_SINGLE_ACTION:
			return WhatWeDoSingleActionPageTemplate
		default: // default in case typename doesn't match
			return () => null
	}
}

export default function Page({ data, preview=false }) {
	const Template = useMemo(() => getTemplate(data), [data])

	return (
		<>
			<PreviewTag visible={preview}/>
			<Template data={data} />
		</>
	)
}

const CATEGORY_TYPE = 'allWhatWeDoCategories'

const pathsQuery = `
	query GetWhatWeDoCategories($locale: SiteLocale) {
		${CATEGORY_TYPE}(
			locale: $locale, 
			filter: { slug: { neq: "${PAGES.WHAT_WE_DO}"  } },
			first: 500
		) {
            pageName
            slug
		}
	}
`;

const categoryPropsQuery = `
	query GetWhatWeDoCategoriesData($slug: String, $locale: SiteLocale)  {
		${CATEGORY_TYPE}(
			locale: $locale, 
			filter: {slug:  {eq: $slug }},
			first: 500
		) {
			__typename
			id
            pageName
            slug
		  	actions: _allReferencingWhatWeDoSingleActions {
			  ${WHAT_WE_DO_SINGLE_ACTION_QUERY}
			  state: chooseState {
			  	${STATE_QUERY}
			  }
			}
            ${SEO_QUERY}
            
			recapInfo {
			 	${RECAP_INFO_QUERY}
			}
			content {
				${RECAP_SUMMARY_ITEM_QUERY}
			}
            seeAlsoInfo{
				${SEE_ALSO_INFO_BLOCK_QUERY}
			}
			isHorizontalAspect
			seeAlsoLinks {
			  ... on ${CONTENT_TYPES.WHAT_WE_DO_SINGLE_ACTION} {
				${WHAT_WE_DO_SINGLE_ACTION_QUERY}
			  }
			  ... on ${CONTENT_TYPES.WHAT_WE_DO_CATEGORY} {
				${WHAT_WE_DO_CATEGORY_QUERY}
			  }
		    }
        }
    }
`

const ACTION_TYPE = 'allWhatWeDoSingleActions'

const actionPathsQuery = `
	query GetWhatWeDoCategories($locale: SiteLocale) {
		${ACTION_TYPE}(
			locale: $locale,
			first: 500
		) {
            slug
		}
	}
`;

const actionPropsQuery = `
	query GetWhatWeDoActionData($slug: String, $locale: SiteLocale)  {
		${ACTION_TYPE}(
			locale: $locale, 
			filter: { slug: { eq: $slug } },
			first: 500
		) {
			__typename
            name
            slug
            ${SEO_QUERY}
			${CONTEXT_BASED_BANNER_QUERY}
			recapInfo{
			 	${RECAP_INFO_QUERY}
			}
			content{
				${RECAP_SUMMARY_ITEM_QUERY}
			}
			seeAlsoInfo{
				${SEE_ALSO_INFO_BLOCK_QUERY}
			}
			isHorizontalAspect
			seeAlsoLinks {
			  ... on ${CONTENT_TYPES.WHAT_WE_DO_SINGLE_ACTION} {
				${WHAT_WE_DO_SINGLE_ACTION_QUERY}
			  }
			  ... on ${CONTENT_TYPES.WHAT_WE_DO_CATEGORY} {
				${WHAT_WE_DO_CATEGORY_QUERY}
			  }
		    }
        }
    }
`

const TYPE_QUERY = `
	query GetWWDType($slug: String, $locale: SiteLocale)  {
		${CATEGORY_TYPE}(
			locale: $locale, 
			filter: { slug: { eq: $slug } },
		) {
			__typename
			slug
        }
        ${ACTION_TYPE}(
			locale: $locale, 
			filter: { slug: { eq: $slug } },
		) {
			__typename
			slug
        }
    }
`


export async function getStaticProps({ params, locale, preview=false }) {
	const { slug } = params;
	try {
		let data = {}
		const [category, actions] = await getWWDType(slug, locale, TYPE_QUERY, [CATEGORY_TYPE, ACTION_TYPE], preview)
		if(category.length > 0){
			//is category
			data = await getPageData(CATEGORY_TYPE, slug, categoryPropsQuery, locale, '', preview);
		} else if(actions.length > 0){
			//is action
			data = await getPageData(ACTION_TYPE, slug, actionPropsQuery, locale, '', preview)
		} else {
			return { notFound: true }; // go to 404
		}

		const layout = await getLayoutData(locale, preview)
		return {
			props: {
				data,
				locale,
				...layout,
				isDarkSearch: true,
				isSingleWhatWeDoPage: getItemType(data) === CONTENT_TYPES.WHAT_WE_DO_SINGLE_ACTION,
				preview
			},
			revalidate: 60 * 2
		};
	} catch (err){
		console.error('getStaticProps error for slug:', params.slug, err);
		return { notFound: true }; // go to 404
	}
}

export async function getStaticPaths({ locales }) {
	const paths = [];
	for (const locale of locales) {
		const categoryPage = await getPathsForLocale(CATEGORY_TYPE, pathsQuery, locale);
		paths.push(...categoryPage);

		const actionPage = await getPathsForLocale(ACTION_TYPE, actionPathsQuery, locale);
		paths.push(...actionPage);
	}

	return {
		paths,
		// fallback: false,
		fallback: 'blocking',
	};
}

async function getPathsForLocale(type, query, locale) {
	const pages = await getPages(type, query, locale);

	return pages.map((page) => {
		return {
			params: {
				slug: page.slug,
			},
			locale,
		};
	}).filter((el) => {
		return el !== null;
	});
}
